import * as THREE from 'three'
import type { Node, NodeBuilder } from 'three/webgpu'
import type { GraphicsBackendKind } from '@/graphics/GraphicsConfig'
import type { MeshClipPacket } from '@/plugins/mesh/clipping/MeshClipPacket'
import {
  MESH_CLIP_HEADER_TEXELS,
  MESH_CLIP_TEXTURE_WIDTH,
} from '@/plugins/mesh/clipping/MeshClipPacket'
import {
  COMPILED_CLIP_EPSILON,
  COMPILED_CLIP_HIGHLIGHT_COLOR,
  CompiledClipVolumeKind,
  MAX_COMPILED_CLIP_GROUPS,
  MAX_COMPILED_CLIP_VOLUMES,
} from '@/shared/clipping/CompiledClipState'
import {
  createDynamicTslClipEvaluation,
  type TSLClipIntUniformNode,
  type TSLClipStateBindingNodes,
  type TSLModule,
} from '@/shared/clipping/TSLClipState'
import { ClipPolygonMaxVerticeCount, ClipTask } from '@/shared/constants'

const VERTEX_COMMON_ANCHOR = '#include <common>'
const VERTEX_PROJECT_ANCHOR = '#include <project_vertex>'
const FRAGMENT_COMMON_ANCHOR = '#include <common>'
const FRAGMENT_CLIP_ANCHOR = '#include <clipping_planes_fragment>'
const FRAGMENT_OUTPUT_ANCHOR = '#include <opaque_fragment>'
const PROGRAM_CACHE_KEY = 'mesh-clip-v1'
const NODE_PROGRAM_CACHE_KEY = 'mesh-clip-node-v1'

interface MeshClipShaderSource {
  uniforms: Record<string, THREE.IUniform>
  vertexShader: string
  fragmentShader: string
}

interface MeshClipNodeMaterial extends THREE.Material {
  setupLighting: (builder: NodeBuilder) => Node
}

/** WebGPU Mesh 裁切所需的 node material 与 TSL 运行时。 */
export interface MeshClipWebGpuMaterialModule {
  readonly MeshBasicNodeMaterial: new () => MeshClipNodeMaterial
  readonly MeshStandardNodeMaterial: new () => MeshClipNodeMaterial
  readonly MeshPhysicalNodeMaterial: new () => MeshClipNodeMaterial
  readonly tsl: TSLModule
}

let webGpuMaterialModule: MeshClipWebGpuMaterialModule | null = null

/** 注册已在 preload 阶段加载的 WebGPU node material 模块。 */
export function registerMeshClipWebGpuMaterialModule(
  module: MeshClipWebGpuMaterialModule | null
): void {
  webGpuMaterialModule = module
}

/** 惰性加载 WebGPU NodeMaterial 与 TSL，避免进入 WebGL2 默认产物。 */
export async function loadMeshClipWebGpuMaterialModule(): Promise<MeshClipWebGpuMaterialModule> {
  const [webgpu, tsl] = await Promise.all([import('three/webgpu'), import('three/tsl')])
  return {
    MeshBasicNodeMaterial:
      webgpu.MeshBasicNodeMaterial as unknown as MeshClipWebGpuMaterialModule['MeshBasicNodeMaterial'],
    MeshStandardNodeMaterial:
      webgpu.MeshStandardNodeMaterial as unknown as MeshClipWebGpuMaterialModule['MeshStandardNodeMaterial'],
    MeshPhysicalNodeMaterial:
      webgpu.MeshPhysicalNodeMaterial as unknown as MeshClipWebGpuMaterialModule['MeshPhysicalNodeMaterial'],
    tsl,
  }
}

function createPlaceholderClipTexture(): THREE.DataTexture {
  const texture = new THREE.DataTexture(
    new Float32Array(4),
    1,
    1,
    THREE.RGBAFormat,
    THREE.FloatType
  )
  texture.name = 'MeshClipPlaceholder'
  texture.minFilter = THREE.NearestFilter
  texture.magFilter = THREE.NearestFilter
  texture.generateMipmaps = false
  texture.flipY = false
  texture.colorSpace = THREE.NoColorSpace
  texture.needsUpdate = true
  return texture
}

/** 所有 Mesh clip 材质共享的逐视图 uniform 绑定。 */
export class MeshClipUniformBinding {
  readonly textureUniform: THREE.IUniform<THREE.DataTexture | null> = { value: null }
  readonly volumeCountUniform: THREE.IUniform<number> = { value: 0 }
  private readonly placeholderTexture = createPlaceholderClipTexture()
  private tslBindingNodes: TSLClipStateBindingNodes | null = null

  bind(packet: MeshClipPacket | null): void {
    this.textureUniform.value = packet?.texture ?? null
    this.volumeCountUniform.value = packet?.volumeCount ?? 0
    if (this.tslBindingNodes) {
      this.tslBindingNodes.texture.value = packet?.texture ?? this.placeholderTexture
      this.tslBindingNodes.volumeCount.value = packet?.volumeCount ?? 0
    }
  }

  clear(): void {
    this.bind(null)
  }

  /** 创建可跨 View 切换 texture/count 的 TSL 绑定。 */
  getTslBindingNodes(tsl: TSLModule): TSLClipStateBindingNodes {
    if (!this.tslBindingNodes) {
      const texture = this.textureUniform.value ?? this.placeholderTexture
      this.tslBindingNodes = {
        texture: tsl.texture(texture, tsl.vec2(0)),
        volumeCount: tsl.uniform(this.volumeCountUniform.value, 'int') as TSLClipIntUniformNode,
      }
    }
    return this.tslBindingNodes
  }

  dispose(): void {
    this.clear()
    this.placeholderTexture.dispose()
  }
}

function vertexDeclarations(): string {
  return `${VERTEX_COMMON_ANCHOR}\nvarying vec3 vMeshClipWorldPosition;`
}

function vertexWorldPosition(): string {
  return `${VERTEX_PROJECT_ANCHOR}
  vec4 meshClipWorldPosition = vec4(transformed, 1.0);
  #ifdef USE_BATCHING
    meshClipWorldPosition = batchingMatrix * meshClipWorldPosition;
  #endif
  #ifdef USE_INSTANCING
    meshClipWorldPosition = instanceMatrix * meshClipWorldPosition;
  #endif
  vMeshClipWorldPosition = (modelMatrix * meshClipWorldPosition).xyz;`
}

function fragmentDeclarations(): string {
  return `${FRAGMENT_COMMON_ANCHOR}
varying vec3 vMeshClipWorldPosition;
uniform sampler2D uMeshClipData;
uniform int uMeshClipVolumeCount;

const float MESH_CLIP_EPSILON = ${COMPILED_CLIP_EPSILON};
const int MESH_CLIP_KIND_POLYGON = ${CompiledClipVolumeKind.POLYGON};
const int MESH_CLIP_KIND_BOX = ${CompiledClipVolumeKind.BOX};
const int MESH_CLIP_TASK_HIGHLIGHT = ${ClipTask.HIGHLIGHT};
const int MESH_CLIP_TASK_SHOW_INSIDE = ${ClipTask.SHOW_INSIDE};
const int MESH_CLIP_TASK_SHOW_OUTSIDE = ${ClipTask.SHOW_OUTSIDE};

vec4 readMeshClipTexel(const int column, const int row) {
  vec2 uv = vec2(
    (float(column) + 0.5) / float(${MESH_CLIP_TEXTURE_WIDTH}),
    (float(row) + 0.5) / float(${MAX_COMPILED_CLIP_VOLUMES})
  );
  return texture2D(uMeshClipData, uv);
}

mat4 readMeshClipMatrix(const int row) {
  return mat4(
    readMeshClipTexel(0, row),
    readMeshClipTexel(1, row),
    readMeshClipTexel(2, row),
    readMeshClipTexel(3, row)
  );
}

bool meshClipPointOnSegment(vec2 point, vec2 start, vec2 end) {
  vec2 edge = end - start;
  vec2 offset = point - start;
  float edgeLengthSq = dot(edge, edge);
  if (edgeLengthSq <= MESH_CLIP_EPSILON * MESH_CLIP_EPSILON) {
    return dot(offset, offset) <= MESH_CLIP_EPSILON * MESH_CLIP_EPSILON;
  }
  float crossValue = edge.x * offset.y - edge.y * offset.x;
  if (abs(crossValue) > MESH_CLIP_EPSILON * max(1.0, sqrt(edgeLengthSq))) {
    return false;
  }
  float projection = dot(offset, edge);
  return projection >= -MESH_CLIP_EPSILON && projection <= edgeLengthSq + MESH_CLIP_EPSILON;
}

bool meshClipPointInPolygon(vec2 point, const int row, const int vertexCount, vec4 bounds) {
  if (
    point.x < bounds.x - MESH_CLIP_EPSILON ||
    point.y < bounds.y - MESH_CLIP_EPSILON ||
    point.x > bounds.z + MESH_CLIP_EPSILON ||
    point.y > bounds.w + MESH_CLIP_EPSILON
  ) {
    return false;
  }

  bool inside = false;
  vec2 previous = readMeshClipTexel(
    ${MESH_CLIP_HEADER_TEXELS} + vertexCount - 1,
    row
  ).xy;
  for (int vertexIndex = 0; vertexIndex < ${ClipPolygonMaxVerticeCount}; vertexIndex++) {
    if (vertexIndex >= vertexCount) break;
    vec2 current = readMeshClipTexel(${MESH_CLIP_HEADER_TEXELS} + vertexIndex, row).xy;
    if (meshClipPointOnSegment(point, previous, current)) return true;
    bool crossesY = (current.y > point.y) != (previous.y > point.y);
    if (crossesY) {
      float intersectionX =
        ((previous.x - current.x) * (point.y - current.y)) /
        (previous.y - current.y) + current.x;
      if (point.x < intersectionX) inside = !inside;
    }
    previous = current;
  }
  return inside;
}

bool meshClipPointInVolume(vec3 worldPoint, const int row, const int kind, const int vertexCount) {
  mat4 worldToClip = readMeshClipMatrix(row);
  if (kind == MESH_CLIP_KIND_BOX) {
    vec3 boxPoint = (worldToClip * vec4(worldPoint, 1.0)).xyz;
    return
      all(greaterThanEqual(boxPoint, vec3(-0.5 - MESH_CLIP_EPSILON))) &&
      all(lessThanEqual(boxPoint, vec3(0.5 + MESH_CLIP_EPSILON)));
  }
  if (kind != MESH_CLIP_KIND_POLYGON) return false;
  vec4 projected = worldToClip * vec4(worldPoint, 1.0);
  if (projected.w <= MESH_CLIP_EPSILON) return false;
  vec2 point = projected.xy / projected.w;
  return meshClipPointInPolygon(point, row, vertexCount, readMeshClipTexel(4, row));
}

void evaluateMeshClip(vec3 worldPoint, out bool hidden, out bool highlighted) {
  hidden = false;
  highlighted = false;
  if (uMeshClipVolumeCount <= 0) return;

  int insideGroupMask = 0;
  int matchedInsideGroupMask = 0;
  for (int volumeIndex = 0; volumeIndex < ${MAX_COMPILED_CLIP_VOLUMES}; volumeIndex++) {
    if (volumeIndex >= uMeshClipVolumeCount) break;
    vec4 metadata = readMeshClipTexel(5, volumeIndex);
    int vertexCount = int(metadata.x + 0.5);
    int clipTask = int(metadata.y + 0.5);
    int groupIndex = int(metadata.z + 0.5);
    int kind = int(metadata.w + 0.5);
    bool inside = meshClipPointInVolume(worldPoint, volumeIndex, kind, vertexCount);

    if (clipTask == MESH_CLIP_TASK_SHOW_OUTSIDE && inside) {
      hidden = true;
      highlighted = false;
      return;
    }
    if (clipTask == MESH_CLIP_TASK_SHOW_INSIDE) {
      int groupBit = 1 << groupIndex;
      insideGroupMask |= groupBit;
      if (inside) matchedInsideGroupMask |= groupBit;
    } else if (clipTask == MESH_CLIP_TASK_HIGHLIGHT && inside) {
      highlighted = true;
    }
  }

  if ((insideGroupMask & matchedInsideGroupMask) != insideGroupMask) {
    hidden = true;
    highlighted = false;
  }
}`
}

function fragmentClipEvaluation(): string {
  return `${FRAGMENT_CLIP_ANCHOR}
  bool meshClipHidden = false;
  bool meshClipHighlighted = false;
  evaluateMeshClip(vMeshClipWorldPosition, meshClipHidden, meshClipHighlighted);
  if (meshClipHidden) discard;`
}

function fragmentHighlight(): string {
  return `if (meshClipHighlighted) {
    outgoingLight = vec3(
      ${COMPILED_CLIP_HIGHLIGHT_COLOR.r},
      ${COMPILED_CLIP_HIGHLIGHT_COLOR.g},
      ${COMPILED_CLIP_HIGHLIGHT_COLOR.b}
    );
  }
  ${FRAGMENT_OUTPUT_ANCHOR}`
}

type MeshClipSourceMaterial = THREE.MeshBasicMaterial | THREE.MeshStandardMaterial

/** 判断材质是否属于首版可安全适配的内建 Mesh 材质。 */
export function isSupportedMeshClipMaterial(
  material: THREE.Material
): material is MeshClipSourceMaterial {
  return (
    material instanceof THREE.MeshBasicMaterial || material instanceof THREE.MeshStandardMaterial
  )
}

/** 将 Mesh clip 代码注入 Three.js 内建 Shader 源。 */
export function patchMeshClipShader(
  shader: MeshClipShaderSource,
  binding: MeshClipUniformBinding
): boolean {
  const hasAnchors =
    shader.vertexShader.includes(VERTEX_COMMON_ANCHOR) &&
    shader.vertexShader.includes(VERTEX_PROJECT_ANCHOR) &&
    shader.fragmentShader.includes(FRAGMENT_COMMON_ANCHOR) &&
    shader.fragmentShader.includes(FRAGMENT_CLIP_ANCHOR) &&
    shader.fragmentShader.includes(FRAGMENT_OUTPUT_ANCHOR)
  if (!hasAnchors) return false

  shader.uniforms.uMeshClipData = binding.textureUniform
  shader.uniforms.uMeshClipVolumeCount = binding.volumeCountUniform
  shader.vertexShader = shader.vertexShader
    .replace(VERTEX_COMMON_ANCHOR, vertexDeclarations())
    .replace(VERTEX_PROJECT_ANCHOR, vertexWorldPosition())
  shader.fragmentShader = shader.fragmentShader
    .replace(FRAGMENT_COMMON_ANCHOR, fragmentDeclarations())
    .replace(FRAGMENT_CLIP_ANCHOR, fragmentClipEvaluation())
    .replace(FRAGMENT_OUTPUT_ANCHOR, fragmentHighlight())
  return true
}

export interface MeshClipMaterialVariant {
  readonly material: THREE.Material
  readonly isSupported: () => boolean
}

function createMeshNodeMaterial(
  source: THREE.MeshBasicMaterial | THREE.MeshStandardMaterial,
  module: MeshClipWebGpuMaterialModule
): MeshClipNodeMaterial | null {
  const MaterialClass =
    source instanceof THREE.MeshPhysicalMaterial
      ? module.MeshPhysicalNodeMaterial
      : source instanceof THREE.MeshBasicMaterial
        ? module.MeshBasicNodeMaterial
        : module.MeshStandardNodeMaterial
  const material = new MaterialClass()
  material.copy(source)
  THREE.Material.prototype.copy.call(material, source)
  return material
}

function patchMeshClipNodeMaterial(
  material: MeshClipNodeMaterial,
  source: THREE.Material,
  binding: MeshClipUniformBinding,
  module: MeshClipWebGpuMaterialModule
): boolean {
  if (typeof material.setupLighting !== 'function') return false

  const sourceProgramCacheKey = source.customProgramCacheKey.bind(source)
  const materialProgramCacheKey = material.customProgramCacheKey.bind(material)
  const baseSetupLighting = material.setupLighting.bind(material)

  material.setupLighting = (builder) => {
    const outgoingLight = baseSetupLighting(builder)
    const evaluation = createDynamicTslClipEvaluation(
      module.tsl,
      binding.getTslBindingNodes(module.tsl),
      module.tsl.positionWorld,
      ClipPolygonMaxVerticeCount,
      MAX_COMPILED_CLIP_VOLUMES
    )
    evaluation.hidden.discard()
    return evaluation.highlighted.select(
      module.tsl.vec3(
        COMPILED_CLIP_HIGHLIGHT_COLOR.r,
        COMPILED_CLIP_HIGHLIGHT_COLOR.g,
        COMPILED_CLIP_HIGHLIGHT_COLOR.b
      ),
      outgoingLight
    )
  }
  material.customProgramCacheKey = () =>
    `${sourceProgramCacheKey()}:${materialProgramCacheKey()}:${NODE_PROGRAM_CACHE_KEY}`
  material.needsUpdate = true
  return true
}

function createMeshClipNodeMaterialVariant(
  source: THREE.MeshBasicMaterial | THREE.MeshStandardMaterial,
  binding: MeshClipUniformBinding,
  onUnsupported: (materialType: string) => void
): MeshClipMaterialVariant | null {
  const module = webGpuMaterialModule
  if (!module) {
    onUnsupported(source.type)
    return null
  }

  const material = createMeshNodeMaterial(source, module)
  if (!material || !patchMeshClipNodeMaterial(material, source, binding, module)) {
    material?.dispose()
    onUnsupported(source.type)
    return null
  }

  return {
    material,
    isSupported: () => true,
  }
}

/** 按后端克隆材质：WebGL2 注入 Shader，WebGPU 创建等价 NodeMaterial。 */
export function createMeshClipMaterialVariant(
  source: THREE.Material,
  binding: MeshClipUniformBinding,
  onUnsupported: (materialType: string) => void,
  backend: GraphicsBackendKind = 'webgl2'
): MeshClipMaterialVariant | null {
  if (!isSupportedMeshClipMaterial(source)) {
    onUnsupported(source.type)
    return null
  }

  if (backend === 'webgpu') {
    return createMeshClipNodeMaterialVariant(source, binding, onUnsupported)
  }

  const material = source.clone()
  const sourceOnBeforeCompile = source.onBeforeCompile.bind(source)
  const sourceProgramCacheKey = source.customProgramCacheKey.bind(source)
  let supported = true

  material.onBeforeCompile = (shader, renderer) => {
    sourceOnBeforeCompile(shader, renderer)
    supported = patchMeshClipShader(shader as MeshClipShaderSource, binding)
    if (!supported) onUnsupported(source.type)
  }
  material.customProgramCacheKey = () => `${sourceProgramCacheKey()}:${PROGRAM_CACHE_KEY}`
  material.needsUpdate = true

  return {
    material,
    isSupported: () => supported,
  }
}

/** 暴露给源码测试的固定 group 容量，防止 GLSL 与 CPU 常量漂移。 */
export const MESH_CLIP_SHADER_GROUP_CAPACITY = MAX_COMPILED_CLIP_GROUPS
